import { useEffect, useRef, useState } from "react";
import {
  AlertTriangle,
  Database,
  Download,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { AdminPage } from "@/components/AdminPage";
import { useToast } from "@/components/Toast";
import { ApiError, fetchJson } from "@podecontratar/shared/lib/api";

interface Backup {
  key: string;
  size: number;
  createdAt: string;
  source: "cron" | "admin" | "unknown";
}

interface RestoreReport {
  tableCounts: Record<string, number>;
  skippedTables: string[];
}

export default function AdminBackupsPage() {
  const [backups, setBackups] = useState<Backup[] | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState<"idle" | "confirm" | "running">(
    "idle"
  );
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [report, setReport] = useState<RestoreReport | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToast();

  async function refresh() {
    try {
      const data = await fetchJson<{ backups: Backup[] }>("/api/admin/backups");
      setBackups(data.backups);
    } catch {
      toast.push("Falha ao carregar backups.", "error");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  async function createNew() {
    setCreating(true);
    try {
      await fetchJson("/api/admin/backups", { method: "POST" });
      toast.push("Backup criado.", "success");
      void refresh();
    } catch {
      toast.push("Não foi possível criar o backup.", "error");
    } finally {
      setCreating(false);
    }
  }

  async function remove(key: string) {
    if (!confirm("Apagar este backup? A ação é irreversível.")) return;
    try {
      await fetchJson(
        `/api/admin/backups?key=${encodeURIComponent(key)}`,
        { method: "DELETE" }
      );
      toast.push("Backup apagado.", "success");
      void refresh();
    } catch {
      toast.push("Não foi possível apagar.", "error");
    }
  }

  function startImport(file: File) {
    setPendingFile(file);
    setImporting("confirm");
  }

  async function confirmImport() {
    if (!pendingFile) return;
    setImporting("running");
    try {
      const fd = new FormData();
      fd.append("backup", pendingFile);
      const data = await fetchJson<{ report: RestoreReport }>(
        "/api/admin/backups/import",
        { method: "POST", form: fd, timeoutMs: 60_000 }
      );
      setReport(data.report);
      toast.push("Restore concluído.", "success");
      setImporting("idle");
      setPendingFile(null);
      void refresh();
    } catch (err) {
      const detail =
        err instanceof ApiError
          ? (err.body as { error?: string } | null)?.error ?? `status_${err.status}`
          : (err as Error).message;
      toast.push(`Falha no restore: ${detail}`, "error");
      setImporting("confirm");
    }
  }

  function cancelImport() {
    setImporting("idle");
    setPendingFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  return (
    <AdminPage
      eyebrow="Operações"
      title="Backups"
      description="Cópias completas do banco D1 em formato JSON, gravadas no R2 sob backups/. Cron diário às 03:00 UTC no worker dedicado; retenção de 30 dias."
      action={
        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) startImport(file);
            }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3.5 py-2 text-sm font-medium text-text transition-colors hover:bg-surface-alt"
          >
            <Upload size={14} strokeWidth={2} aria-hidden />
            Importar
          </button>
          <button
            type="button"
            onClick={createNew}
            disabled={creating}
            className="inline-flex items-center gap-1.5 rounded-lg bg-accent px-3.5 py-2 text-sm font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px disabled:opacity-50"
          >
            {creating ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Plus size={14} strokeWidth={2.25} aria-hidden />
            )}
            Novo backup
          </button>
        </div>
      }
    >
      {backups === null ? (
        <div className="flex items-center justify-center rounded-3xl border border-border bg-surface p-12">
          <Loader2 size={20} className="animate-spin text-text-tertiary" />
        </div>
      ) : backups.length === 0 ? (
        <EmptyState />
      ) : (
        <section className="rounded-3xl border border-border bg-surface">
          <ul className="divide-y divide-border">
            {backups.map((b) => (
              <BackupRow key={b.key} b={b} onDelete={() => remove(b.key)} />
            ))}
          </ul>
        </section>
      )}

      {importing !== "idle" && pendingFile && (
        <ConfirmImportDialog
          file={pendingFile}
          running={importing === "running"}
          onConfirm={confirmImport}
          onCancel={cancelImport}
        />
      )}

      {report && (
        <RestoreReportDialog report={report} onClose={() => setReport(null)} />
      )}
    </AdminPage>
  );
}

// ---------------------------------------------------------------------------

function BackupRow({ b, onDelete }: { b: Backup; onDelete: () => void }) {
  const downloadHref = `/api/admin/backups/download?key=${encodeURIComponent(b.key)}`;
  return (
    <li className="flex flex-wrap items-center justify-between gap-4 p-5">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="truncate font-mono text-xs text-text">{b.key}</p>
          <SourceBadge source={b.source} />
        </div>
        <p className="mt-1 text-[11px] text-text-tertiary">
          {new Date(b.createdAt).toLocaleString("pt-BR")} ·{" "}
          {formatBytes(b.size)}
        </p>
      </div>
      <div className="flex gap-2">
        <a
          href={downloadHref}
          download
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text transition-colors hover:bg-surface-alt"
        >
          <Download size={12} strokeWidth={2} aria-hidden />
          Baixar
        </a>
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-surface px-3 py-1.5 text-xs font-medium text-text-secondary transition-colors hover:border-danger/40 hover:text-danger"
        >
          <Trash2 size={12} strokeWidth={2} aria-hidden />
          Apagar
        </button>
      </div>
    </li>
  );
}

function SourceBadge({ source }: { source: Backup["source"] }) {
  if (source === "cron") {
    return (
      <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-accent">
        auto
      </span>
    );
  }
  if (source === "admin") {
    return (
      <span className="rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-text-secondary">
        manual
      </span>
    );
  }
  return null;
}

function EmptyState() {
  return (
    <div className="rounded-3xl border border-border bg-surface p-10 text-center">
      <Database size={28} className="mx-auto text-text-tertiary" />
      <p className="mt-3 text-sm text-text-secondary">
        Nenhum backup ainda. O cron diário às 03:00 UTC gera o primeiro
        automaticamente, ou clique em <strong className="text-text">Novo backup</strong>{" "}
        pra forçar agora.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------

function ConfirmImportDialog({
  file,
  running,
  onConfirm,
  onCancel,
}: {
  file: File;
  running: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <DialogShell title="Restaurar backup" onClose={running ? undefined : onCancel}>
      <div className="flex items-start gap-3 rounded-xl border border-danger/40 bg-danger/5 p-4">
        <AlertTriangle size={18} className="mt-0.5 shrink-0 text-danger" />
        <div className="text-sm text-text">
          <p className="font-semibold">Operação destrutiva.</p>
          <p className="mt-1 text-text-secondary">
            Todas as linhas das tabelas presentes no arquivo serão{" "}
            <strong className="text-text">apagadas</strong> e substituídas pelo
            conteúdo do backup. Não há rollback automático — recomenda-se criar
            um novo backup antes.
          </p>
        </div>
      </div>
      <dl className="mt-5 space-y-1 text-sm text-text-secondary">
        <div className="flex justify-between gap-3">
          <dt>Arquivo</dt>
          <dd className="truncate font-mono text-text">{file.name}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Tamanho</dt>
          <dd className="font-mono text-text">{formatBytes(file.size)}</dd>
        </div>
      </dl>
      <div className="mt-7 flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          disabled={running}
          className="rounded-lg border border-border bg-surface px-4 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-alt disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={onConfirm}
          disabled={running}
          className="inline-flex items-center gap-1.5 rounded-lg bg-danger px-4 py-2 text-sm font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-danger/90 active:translate-y-px disabled:opacity-50"
        >
          {running && <Loader2 size={14} className="animate-spin" />}
          {running ? "Restaurando…" : "Restaurar agora"}
        </button>
      </div>
    </DialogShell>
  );
}

function RestoreReportDialog({
  report,
  onClose,
}: {
  report: RestoreReport;
  onClose: () => void;
}) {
  const total = Object.values(report.tableCounts).reduce((a, b) => a + b, 0);
  return (
    <DialogShell title="Restore concluído" onClose={onClose}>
      <p className="text-sm text-text-secondary">
        {total.toLocaleString("pt-BR")} linhas em{" "}
        {Object.keys(report.tableCounts).length} tabela(s).
        {report.skippedTables.length > 0 && (
          <>
            {" "}
            {report.skippedTables.length} tabela(s) ignorada(s) (não existem no
            schema atual).
          </>
        )}
      </p>
      <div className="mt-5 max-h-80 overflow-y-auto rounded-xl border border-border bg-surface-alt p-3">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-semibold uppercase tracking-[0.18em] text-text-tertiary">
              <th className="py-1.5">Tabela</th>
              <th className="py-1.5 text-right">Linhas</th>
            </tr>
          </thead>
          <tbody className="font-mono text-xs">
            {Object.entries(report.tableCounts)
              .sort()
              .map(([table, count]) => (
                <tr key={table} className="border-t border-border">
                  <td className="py-1.5 text-text">{table}</td>
                  <td className="py-1.5 text-right text-text-secondary">
                    {count.toLocaleString("pt-BR")}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      {report.skippedTables.length > 0 && (
        <p className="mt-4 text-xs text-text-tertiary">
          Ignoradas: {report.skippedTables.join(", ")}
        </p>
      )}
      <div className="mt-6 flex justify-end">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg bg-accent px-3.5 py-2 text-xs font-medium text-text-inverse transition-[background-color,transform] duration-150 hover:bg-accent-hover active:translate-y-px"
        >
          Fechar
        </button>
      </div>
    </DialogShell>
  );
}

// ---------------------------------------------------------------------------

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="w-full max-w-xl rounded-3xl border border-border bg-surface p-7 shadow-[var(--shadow-card-lift)]">
        <div className="flex items-start justify-between gap-4">
          <h2 className="text-base font-semibold tracking-tight text-text">
            {title}
          </h2>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              aria-label="Fechar"
              className="rounded-lg p-1.5 text-text-tertiary transition-colors hover:bg-surface-alt hover:text-text"
            >
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
          )}
        </div>
        <div className="mt-5">{children}</div>
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KiB`;
  return `${(n / 1024 / 1024).toFixed(1)} MiB`;
}
